import { DataStoreMockPlugin } from 'ui-common';
import { CompositionStore } from '../CompositionStore';
import { Composition, CompositionRequest, CompositionPublishMetadataRequest, CompositionPublishMetadata, CompositionBuilderRequest } from '../interfaces';

export class CompositionStoreMockPlugin extends DataStoreMockPlugin<CompositionStore> {
  mocks: any;
  mockData: Composition[] | null = null;
  mockApis: string[] = [
    ...DataStoreMockPlugin.crudMockApis,
    'getCompositions',
    'getComposition',
    'getCompositionForBuilder',
    'createComposition',
    'updateComposition',
    'updateCompositionBuilder',
    'deleteComposition',
    'deleteAllCompositionVersions',
    'restoreComposition',
    'publishComposition',
    'getPublishedComposition',
    'getLatestComposition',
    'getCompositionHistory'
  ];

  initialize(): void {
    this.applyMocks();
  }

  /**
   * Load mock composition data
   * Creates a single deep copy per plugin instance to prevent test pollution
   */
  async getMockData() {
    if (!this.mockData) {
      this.mocks ??= await import('./composition-store-mock-data');
      this.mockData = JSON.parse(JSON.stringify(this.mocks.data));
    }
    return this.mockData;
  }

  /**
   * Mock implementation of getCompositions
   * Returns array of compositions
   */
  async getCompositions() {
    const data = await this.getMockData();
    this.store.setData(data, { action: 'load' });
    return data;
  }

  /**
   * Mock implementation of getComposition
   * Returns single composition by ID
   */
  async getComposition(compositionId: string) {
    const data = await this.getMockData();
    return data.find((comp: Composition) => comp.id === compositionId) || null;
  }

  /**
   * Mock implementation of getCompositionForBuilder
   * Returns composition for builder view (same as getComposition in mock)
   */
  async getCompositionForBuilder(compositionId: string) {
    const data = await this.getMockData();
    return data.find((comp: Composition) => comp.id === compositionId) || null;
  }

  /**
   * Mock implementation of createComposition
   * Creates a new composition
   */
  async createComposition(compositionData: CompositionRequest) {
    const id = `comp-${Date.now()}`;
    const newComposition: Composition = {
      id,
      name: compositionData.name || `composition-${id}`,
      description: compositionData.description,
      initialId: compositionData.initialId || id,
      versionNumber: compositionData.versionNumber || 1,
      published: false,
      restored: false,
      changeMetadata: compositionData.changeMetadata || 'Created via API',
      createdBy: 'mock-user@example.com',
      creationDate: new Date().toISOString(),
      lastModifiedBy: 'mock-user@example.com',
      lastModified: new Date().toISOString()
    };

    const data = await this.getMockData();
    data.push(newComposition);
    return newComposition;
  }

  /**
   * Mock implementation of updateComposition
   * Updates an existing composition
   */
  async updateComposition(compositionId: string, compositionData: CompositionRequest) {
    const data = await this.getMockData();
    const index = data.findIndex((comp: Composition) => comp.id === compositionId);

    if (index === -1) {
      throw new Error(`Composition ${compositionId} not found`);
    }

    const updated: Composition = {
      ...data[index],
      ...compositionData,
      lastModifiedBy: 'mock-user@example.com',
      lastModified: new Date().toISOString()
    };

    data[index] = updated;
    return updated;
  }

  /**
   * Mock implementation of updateCompositionBuilder
   * Updates composition builder configuration
   */
  async updateCompositionBuilder(compositionId: string, builderData: CompositionBuilderRequest) {
    const data = await this.getMockData();
    const index = data.findIndex((comp: Composition) => comp.id === compositionId);

    if (index === -1) {
      throw new Error(`Composition ${compositionId} not found`);
    }

    // Create mock assets if logoId or faviconId are provided
    const logo = builderData.logoId ? {
      id: builderData.logoId,
      name: `logo-${builderData.logoId}.png`,
      mimeType: 'image/png',
      fileSize: 25600,
      publicUrl: `https://cdn.example.com/assets/${builderData.logoId}`
    } : data[index].logo;

    const favicon = builderData.faviconId ? {
      id: builderData.faviconId,
      name: `favicon-${builderData.faviconId}.ico`,
      mimeType: 'image/x-icon',
      fileSize: 1024,
      publicUrl: `https://cdn.example.com/assets/${builderData.faviconId}`
    } : data[index].favicon;

    const updated: Composition = {
      ...data[index],
      name: builderData.name,
      description: builderData.description,
      initialId: builderData.initialId || data[index].initialId,
      versionNumber: builderData.versionNumber || data[index].versionNumber,
      changeMetadata: builderData.changeMetadata,
      publishedPath: builderData.publishedPath,
      hostProvider: builderData.hostProvider,
      uiAddress: builderData.uiAddress,
      logo,
      favicon,
      language: builderData.language,
      timezone: builderData.timezone,
      dateTimeFormat: builderData.dateTimeFormat,
      lastModifiedBy: 'mock-user@example.com',
      lastModified: new Date().toISOString()
    };

    // Note: theme, authSettings, pages, and signalStoreId from CompositionBuilderRequest
    // are builder-specific configurations that don't directly map to Composition fields.
    // In a real implementation, these would be processed and stored separately.

    data[index] = updated;
    return updated;
  }

  /**
   * Mock implementation of deleteComposition
   * Deletes a single composition version
   */
  async deleteComposition(compositionId: string) {
    const data = await this.getMockData();
    const index = data.findIndex((comp: Composition) => comp.id === compositionId);

    if (index !== -1) {
      const deletedComposition = data[index];
      data.splice(index, 1);

      // Update store's cached data
      this.store.setData(data, { action: 'delete', item: deletedComposition });

      // Clear current composition if it's the one being deleted
      if (this.store.getCurrentComposition()?.id === compositionId) {
        this.store.clearCurrentComposition();
      }
    }
  }

  /**
   * Mock implementation of deleteAllCompositionVersions
   * Deletes all versions of a composition by initialId
   */
  async deleteAllCompositionVersions(compositionId: string) {
    const data = await this.getMockData();

    // Find the composition to get its initialId
    const composition = data.find((comp: Composition) => comp.id === compositionId);
    if (!composition) return;

    const initialId = composition.initialId || compositionId;

    // Find all compositions to delete
    const deletedCompositions = data.filter((comp: Composition) => comp.initialId === initialId);

    // Remove all compositions with the same initialId
    const filtered = data.filter((comp: Composition) => comp.initialId !== initialId);
    data.length = 0;
    data.push(...filtered);

    // Update store's cached data
    this.store.setData(data, { action: 'delete-all-versions', item: deletedCompositions });

    // Clear current composition if it's one of the deleted versions
    const currentComp = this.store.getCurrentComposition();
    if (currentComp) {
      const currentInitialId = currentComp.initialId || currentComp.id;
      if (currentInitialId === initialId) {
        this.store.clearCurrentComposition();
      }
    }
  }

  /**
   * Mock implementation of restoreComposition
   * Restores a composition version (creates a new version based on the specified one)
   */
  async restoreComposition(compositionId: string) {
    const data = await this.getMockData();
    const source = data.find((comp: Composition) => comp.id === compositionId);

    if (!source) {
      throw new Error(`Composition ${compositionId} not found`);
    }

    // Find the highest version number for this initialId
    const versions = data.filter((comp: Composition) => comp.initialId === source.initialId);
    const maxVersion = Math.max(...versions.map((v: Composition) => v.versionNumber || 0));

    const restored: Composition = {
      ...source,
      id: `${source.initialId}-restored-${Date.now()}`,
      versionNumber: maxVersion + 1,
      published: false,
      restored: true,
      changeMetadata: `Restored from version ${source.versionNumber} (ID: ${source.id})`,
      createdBy: 'mock-user@example.com',
      creationDate: new Date().toISOString(),
      lastModifiedBy: 'mock-user@example.com',
      lastModified: new Date().toISOString()
    };

    data.push(restored);
    return restored;
  }

  /**
   * Mock implementation of publishComposition
   * Publishes a composition version
   */
  async publishComposition(compositionId: string, publishData: CompositionPublishMetadataRequest): Promise<CompositionPublishMetadata> {
    const data = await this.getMockData();
    const composition = data.find((comp: Composition) => comp.id === compositionId);

    if (!composition) {
      throw new Error(`Composition ${compositionId} not found`);
    }

    // Update the composition to published
    composition.published = true;
    composition.lastPublishedBy = 'mock-user@example.com';
    composition.lastPublished = new Date().toISOString();
    composition.publishedPath = `/${publishData.compositionName}`;

    // Return publication response
    return {
      id: `pub-${Date.now()}`,
      compositionId: composition.id,
      clientName: publishData.clientName,
      compositionName: publishData.compositionName,
      title: publishData.title,
      description: publishData.description,
      host: `${publishData.clientName}.ui.lucidworks.com/${publishData.compositionName}`,
      createdBy: 'mock-user@example.com',
      creationDate: new Date().toISOString()
    };
  }

  /**
   * Mock implementation of getPublishedComposition
   * Returns the published version of a composition group
   */
  async getPublishedComposition(compositionId: string) {
    const data = await this.getMockData();

    // Find the composition to get its initialId
    const composition = data.find((comp: Composition) => comp.id === compositionId);
    if (!composition) {
      throw new Error(`Composition ${compositionId} not found`);
    }

    const initialId = composition.initialId || compositionId;

    // Find the published version with the same initialId
    return data.find((comp: Composition) =>
      comp.initialId === initialId && comp.published === true
    ) || null;
  }

  /**
   * Mock implementation of getLatestComposition
   * Returns the latest version of a composition group
   */
  async getLatestComposition(compositionId: string) {
    const data = await this.getMockData();

    // Find the composition to get its initialId
    const composition = data.find((comp: Composition) => comp.id === compositionId);
    if (!composition) {
      throw new Error(`Composition ${compositionId} not found`);
    }

    const initialId = composition.initialId || compositionId;

    // Find all versions with the same initialId and return the one with highest version number
    const versions = data.filter((comp: Composition) => comp.initialId === initialId);
    return versions.reduce((latest: Composition, current: Composition) =>
      (current.versionNumber || 0) > (latest.versionNumber || 0) ? current : latest
    );
  }

  /**
   * Mock implementation of getCompositionHistory
   * Returns all versions of a composition, ordered by version number descending
   */
  async getCompositionHistory(compositionId: string) {
    const data = await this.getMockData();

    // Find the composition to get its initialId
    const composition = data.find((comp: Composition) => comp.id === compositionId);
    if (!composition) {
      return [];
    }

    const initialId = composition.initialId || compositionId;

    // Find all versions with the same initialId
    const versions = data.filter((comp: Composition) => comp.initialId === initialId);

    // Sort by version number descending (newest first)
    return versions.sort((a: Composition, b: Composition) =>
      (b.versionNumber || 0) - (a.versionNumber || 0)
    );
  }
}
